import {
  STAFF_IMPORT_BOUNDS,
  type StaffImportJob,
  type StaffImportMappingIssueCode,
  type StaffImportParseErrorCode,
  type staffImportColumnNotes,
  type staffImportDelimiters,
  type staffImportEncodings,
  type staffImportTargetGroups,
} from "@lafayette/schemas";

// Every sentence the importer shows about a refused file, a refused mapping or a job comes from a CODE
// in this file. Nothing here (and nothing built from it) copies a cell, a header or a file name, so no
// message can leak source text into an alert or a log. A `Record` over the contract's code tuple makes
// the compiler fail when the contract gains a code that has no sentence.

const number = new Intl.NumberFormat("en-US");

export const parseErrorMessages: Readonly<Record<StaffImportParseErrorCode, string>> = {
  UNSUPPORTED_FORMAT: "This file type is not supported. Use a .csv, .tsv, .xlsx or .xls file.",
  SIGNATURE_MISMATCH:
    "The content of this file does not match its file type. Save it again as CSV or Excel and upload it again.",
  INVALID_ENCODING:
    "This text file is not UTF-8 or UTF-16. Save it as CSV UTF-8 and upload it again.",
  TOO_MANY_ROWS: `The file has more than ${number.format(STAFF_IMPORT_BOUNDS.maxRows)} data rows. Split it into smaller files.`,
  TOO_MANY_COLUMNS: `The file has more than ${STAFF_IMPORT_BOUNDS.maxColumns} columns.`,
  CELL_TOO_LONG: `A cell has more than ${number.format(STAFF_IMPORT_BOUNDS.maxCellChars)} characters.`,
  ROW_TOO_LARGE: "A row holds too much text. Shorten the long cells in this row.",
  ARCHIVE_LIMIT: "This Excel file is too large or too complex to read safely.",
  MALFORMED_FILE: "The file is damaged, or it is not in the format its name says.",
  EMPTY_FILE: "The file has no data.",
  FILE_TOO_LARGE: "The file is larger than 10 MB.",
  SHEET_NOT_FOUND: "The chosen sheet was not found in the file.",
  HEADER_ROW_NOT_FOUND: "The chosen header row was not found in the sheet.",
};

export const mappingIssueMessages: Readonly<Record<StaffImportMappingIssueCode, string>> = {
  COLUMN_OUT_OF_RANGE: "A decision points at a column that does not exist.",
  COLUMN_DUPLICATE: "A column has more than one decision.",
  COLUMN_MISSING: "A column has no decision.",
  DECISION_SHAPE: "A column decision is not complete. A mapped column needs a field.",
  TARGET_UNKNOWN: "A column is mapped to a field that does not exist.",
  TARGET_DUPLICATE: "Another column already maps to this field. Only one column can fill it.",
  COLUMN_UNDECIDED: "This column is not mapped yet. Map it to a field or ignore it.",
  NAME_NOT_MAPPED: "Map one column to Name.",
  ITEM_TYPE_MISSING: "Map a column to Item type, or choose one item type for the whole import.",
  ITEM_TYPE_CONFLICT:
    "A column is mapped to Item type, so you cannot also choose one item type for the whole import.",
};

/** The text after "Column C · header": the word that tells staff where a parse error points. */
export function describeParseErrorPosition(error: {
  row: number | null;
  column: number | null;
}): string | null {
  if (error.row !== null && error.column !== null) {
    return `Row ${error.row}, column ${error.column}`;
  }
  if (error.row !== null) return `Row ${error.row}`;
  if (error.column !== null) return `Column ${error.column}`;
  return null;
}

export const targetGroupLabels: Readonly<Record<(typeof staffImportTargetGroups)[number], string>> =
  {
    common: "Common fields",
    event: "Event fields",
    dining: "Dining fields",
    villa: "Villa fields",
    experience: "Experience fields",
    service: "Service fields",
  };

export const encodingLabels: Readonly<Record<(typeof staffImportEncodings)[number], string>> = {
  UTF8: "UTF-8",
  UTF8_BOM: "UTF-8 with a byte order mark",
  UTF16LE: "UTF-16 little-endian",
  UTF16BE: "UTF-16 big-endian",
  NOT_APPLICABLE: "Not applicable",
};

export const delimiterLabels: Readonly<Record<(typeof staffImportDelimiters)[number], string>> = {
  COMMA: "Comma",
  SEMICOLON: "Semicolon",
  TAB: "Tab",
  NOT_APPLICABLE: "Not applicable",
};

export const columnNoteLabels: Readonly<Record<(typeof staffImportColumnNotes)[number], string>> = {
  DUPLICATE_HEADER: "Another column has the same header",
  BLANK_HEADER: "No header",
  FORMULA_CELLS: "Has formula cells (the saved value is used)",
  DATE_FORMATTED: "Date format",
  EMPTY_COLUMN: "Empty column",
};

export type JobStatusView = Readonly<{
  label: string;
  glyph: string;
  tone: "draft" | "submitted" | "done" | "failed" | "cancelled";
}>;

/** A job status as a word and a glyph, so it never depends on colour alone. */
export function describeJobStatus(
  job: Pick<StaffImportJob, "status" | "failureCode">,
): JobStatusView {
  switch (job.status) {
    case "UPLOADED":
    case "MAPPING":
      return { label: "Draft", glyph: "✎", tone: "draft" };
    case "VALIDATING":
      return { label: "Submitted, waiting for validation", glyph: "⏳", tone: "submitted" };
    case "REVIEW_REQUIRED":
      return { label: "Review required", glyph: "⏳", tone: "submitted" };
    case "APPROVED":
    case "PROCESSING":
      return { label: "In progress", glyph: "⏳", tone: "submitted" };
    case "COMPLETED":
      return { label: "Completed", glyph: "✓", tone: "done" };
    case "FAILED":
      return job.failureCode === "REJECTED_BY_STAFF"
        ? { label: "Cancelled", glyph: "–", tone: "cancelled" }
        : { label: "Failed", glyph: "⚠", tone: "failed" };
  }
}

/** Whether staff may still change the mapping of a job. */
export function isEditableJob(job: Pick<StaffImportJob, "status">): boolean {
  return job.status === "MAPPING";
}

export const mediaTypeLabels = {
  CSV: "CSV",
  TSV: "TSV",
  XLS: "Excel 97-2003 (.xls)",
  XLSX: "Excel (.xlsx)",
} as const;

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
